import { useCallback, useEffect, useMemo, useState } from "react";
import { container } from "tsyringe";
import { match } from "ts-pattern";

import DefaultAvatar from "assets/images/default-avatar.svg";
import AnthonyGranierAvatar from "assets/images/featuredProjects/grandsaut/anthony_granier.png";
import CBPartners from "assets/images/featuredProjects/malgremoi/cb_partners.jpg";
import CharlotteGabrisAvatar from "assets/images/featuredProjects/friends/charlotte_gabris.png";
import ClaraLecoeurAvatar from "assets/images/featuredProjects/potus/clara-lecoeur.png";
import DanielGibersteinAvatar from "assets/images/featuredProjects/dim/daniel-giberstein.png";
import DesireeAvatar from "assets/images/featuredProjects/snowvision/DesireeAvatar.png";
import LucLepretreAvatar from "assets/images/featuredProjects/clubvip/luc-lepretre.png";
import MarzaAvatar from "assets/images/featuredProjects/trainee/marza.png";
import PhilippeGrayAvatar from "assets/images/featuredProjects/kdanse/philippe_gray.png";
import SebastienPraicheuxAvatar from "assets/images/featuredProjects/lattente/sebastien_praicheux.jpg";
import SylvainSaintJamesAvatar from "assets/images/featuredProjects/rajeuniroumourir/sylvain-saint-james.png";
import ThierryMauvignierAvatar from "assets/images/featuredProjects/bataillonsilencieux/thierry-mauvignier.jpg";

import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";

import I18n from "components/materials/I18n";
import { usePoster } from "components/pages/Project/ProjectTemplate/hooks/usePoster";
import ContributorService from "services/projects/ContributorService";
import { formatDate } from "utils/DateUtils";
import ProjectCard from "../ProjectCard";

const contributorService = container.resolve(ContributorService);

type IProps = {
	project: ProjectResponseResource;
};

export default function ProjectCardFactory(props: IProps) {
	const { poster, title, description, contributor, likes, metadata } = useProject(props.project);

	if (!poster) {
		return null;
	}

	return <ProjectCard projectId={props.project.id} title={title} description={description} poster={poster} contributor={contributor} likes={likes} tags={metadata} />;
}

export const useProject = (project: ProjectResponseResource) => {
	const { poster } = usePoster(project);
	const { projectLeaderName, projectLeaderAvatar } = useProjectLeader(project);

	const title = useMemo(
		() =>
			match(I18n.getLang())
				.with("fr", () => project.title)
				.otherwise(() => project.title_en ?? ""),
		[project.title, project.title_en],
	);

	const description = useMemo(
		() =>
			match(I18n.getLang())
				.with("fr", () => project.pitch)
				.otherwise(() => project.pitch_en ?? ""),
		[project.pitch, project.pitch_en],
	);

	const likes = useMemo(() => {
		return project.likes.length;
	}, [project]);

	const contributor = useMemo(
		() =>
			match(project.componentName)
				.with("Dim", () => ({
					avatar: { url: DanielGibersteinAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.dim.team.project_leader.name),
				}))
				.with("MalgreMoi", () => ({
					avatar: { url: CBPartners },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.malgre_moi.team.project_leader.name),
				}))
				.with("Friends", () => ({
					avatar: { url: CharlotteGabrisAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.friends.team.project_leader.name),
				}))
				.with("GrandSaut", () => ({
					avatar: { url: AnthonyGranierAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.grand_saut.team.project_leader.name),
				}))
				.with("KDanse", () => ({
					avatar: { url: PhilippeGrayAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.kdanse.team.project_leader.name),
				}))
				.with("LAttente", () => ({
					avatar: { url: SebastienPraicheuxAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.lattente.team.project_leader.name),
				}))
				.with("SnowVision", () => ({
					avatar: { url: DesireeAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.snow_vision.team.project_leader.name),
				}))
				.with("BataillonSilencieux", () => ({
					avatar: { url: ThierryMauvignierAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.bataillon_silencieux.team.project_leader.name),
				}))
				.with("RajeunirOuMourir", () => ({
					avatar: { url: SylvainSaintJamesAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.rajeunir_ou_mourir.team.project_leader.name),
				}))
				.with("Potus", () => ({
					avatar: { url: ClaraLecoeurAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.potus.team.project_leader.name),
				}))
				.with("ClubVip", () => ({
					avatar: { url: LucLepretreAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.club_vip.team.project_leader.name),
				}))
				.with("Trainee", () => ({
					avatar: { url: MarzaAvatar },
					name: I18n.trslt(I18n.asset.pages.featuredProjects.trainee.team.project_leader.name),
				}))
				.otherwise(() => ({
					avatar: { url: projectLeaderAvatar ?? DefaultAvatar },
					name: projectLeaderName,
				})),
		[project.componentName, projectLeaderAvatar, projectLeaderName],
	);

	const trimDate = (dateString: string) => {
		return dateString.replace(/^le\s+/i, "");
	};

	const metadata = useMemo(
		() =>
			match(project.componentName)
				.with("Dim", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.dim.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.dim.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.dim.header.properties.format.value),
				}))
				.with("MalgreMoi", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.malgre_moi.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.malgre_moi.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.malgre_moi.header.properties.format.value),
				}))
				.with("Friends", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.friends.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.friends.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.friends.header.properties.format.value),
				}))
				.with("GrandSaut", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.grand_saut.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.grand_saut.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.grand_saut.header.properties.format.value),
				}))
				.with("KDanse", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.kdanse.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.kdanse.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.kdanse.header.properties.format.value),
				}))
				.with("LAttente", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.lattente.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.lattente.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.lattente.header.properties.format.value),
				}))
				.with("SnowVision", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.snow_vision.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.snow_vision.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.snow_vision.header.properties.format.value),
				}))
				.with("BataillonSilencieux", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.bataillon_silencieux.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.bataillon_silencieux.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.bataillon_silencieux.header.properties.format.value),
				}))
				.with("RajeunirOuMourir", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.rajeunir_ou_mourir.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.rajeunir_ou_mourir.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.rajeunir_ou_mourir.header.properties.format.value),
				}))
				.with("Potus", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.potus.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.potus.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.potus.header.properties.format.value),
				}))
				.with("ClubVip", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.club_vip.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.club_vip.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.club_vip.header.properties.format.value),
				}))
				.with("Trainee", () => ({
					publicationDate: trimDate(I18n.trslt(I18n.asset.pages.featuredProjects.trainee.header.properties.publication.value)),
					genre: I18n.trslt(I18n.asset.pages.featuredProjects.trainee.header.properties.genre.value),
					format: I18n.trslt(I18n.asset.pages.featuredProjects.trainee.header.properties.format.value),
				}))
				.otherwise(() => ({
					publicationDate: formatDate(I18n.getLocale(), new Date(project.publishedAt ?? project.createdAt ?? "")),
					genre: I18n.trslt(I18n.asset.enums.EProjectThemes[project.theme]),
					format: I18n.trslt(I18n.asset.enums.EProjectFormats[project.format]),
				})),
		[project.componentName, project.createdAt, project.format, project.publishedAt, project.theme],
	);

	return { poster, title, description, contributor, likes, metadata };
};

export function useProjectLeader(project: ProjectResponseResource) {
	const [projectLeaderAvatar, setProjectLeaderAvatar] = useState<string | null>(null);

	const clearProjectLeaderAvatar = useCallback(() => {
		setProjectLeaderAvatar(DefaultAvatar);
	}, []);

	const fetchProjectLeaderAvatar = useCallback(() => {
		if (!project.projectTeam?.projectLeader?.avatar) return clearProjectLeaderAvatar();

		return contributorService.downloadAvatar(project.id, project.projectTeam.id, project.projectTeam.projectLeader.id).then((avatar) => {
			if (!avatar) return clearProjectLeaderAvatar();
			return setProjectLeaderAvatar(URL.createObjectURL(avatar));
		});
	}, [clearProjectLeaderAvatar, project]);

	const projectLeaderName = useMemo(() => getProjectLeaderName(project), [project]);

	useEffect(() => {
		if (!project.projectTeam?.projectLeader) return;
		fetchProjectLeaderAvatar();
	}, [project, fetchProjectLeaderAvatar]);

	return { projectLeaderName, projectLeaderAvatar };
}

export const getPublicationDate = (project: ProjectResponseResource) => {
	return match(project.componentName)
		.with("Dim", () => new Date("February 5, 2025"))
		.with("MalgreMoi", () => new Date("May 10, 2024"))
		.with("Friends", () => new Date("May 10, 2024"))
		.with("GrandSaut", () => new Date("May 10, 2024"))
		.with("KDanse", () => new Date("May 10, 2024"))
		.with("LAttente", () => new Date("December 10, 2024"))
		.with("SnowVision", () => new Date("March 3, 2025"))
		.with("BataillonSilencieux", () => new Date("March 13, 2025"))
		.with("RajeunirOuMourir", () => new Date("March 17, 2025"))
		.with("Potus", () => new Date("March 24, 2025"))
		.with("ClubVip", () => new Date("March 31, 2025"))
		.with("Trainee", () => new Date("April 7, 2025"))
		.otherwise(() => new Date(project.publishedAt ?? project.createdAt ?? ""));
};

export const getProjectLeaderName = (project: ProjectResponseResource) => {
	return match(project.componentName)
		.with("Dim", () => I18n.trslt(I18n.asset.pages.featuredProjects.dim.team.project_leader.name))
		.with("MalgreMoi", () => I18n.trslt(I18n.asset.pages.featuredProjects.malgre_moi.team.project_leader.name))
		.with("Friends", () => I18n.trslt(I18n.asset.pages.featuredProjects.friends.team.project_leader.name))
		.with("GrandSaut", () => I18n.trslt(I18n.asset.pages.featuredProjects.grand_saut.team.project_leader.name))
		.with("KDanse", () => I18n.trslt(I18n.asset.pages.featuredProjects.kdanse.team.project_leader.name))
		.with("LAttente", () => I18n.trslt(I18n.asset.pages.featuredProjects.lattente.team.project_leader.name))
		.with("SnowVision", () => I18n.trslt(I18n.asset.pages.featuredProjects.snow_vision.team.project_leader.name))
		.with("BataillonSilencieux", () => I18n.trslt(I18n.asset.pages.featuredProjects.bataillon_silencieux.team.project_leader.name))
		.with("RajeunirOuMourir", () => I18n.trslt(I18n.asset.pages.featuredProjects.rajeunir_ou_mourir.team.project_leader.name))
		.with("Potus", () => I18n.trslt(I18n.asset.pages.featuredProjects.potus.team.project_leader.name))
		.with("ClubVip", () => I18n.trslt(I18n.asset.pages.featuredProjects.club_vip.team.project_leader.name))
		.with("Trainee", () => I18n.trslt(I18n.asset.pages.featuredProjects.trainee.team.project_leader.name))
		.otherwise(() => {
			const leader = project.projectTeam?.projectLeader;
			if (leader?.firstName || leader?.lastName) {
				return [leader.firstName ?? "", leader.lastName ?? ""].join(" ").trim();
			}
			if (leader?.companyName) {
				return leader.companyName;
			}
			if (project?.author?.firstName || project?.author?.lastName) {
				return [project.author.firstName ?? "", project.author.lastName ?? ""].join(" ").trim();
			}
			return "";
		});
};
